import React, { useRef, useState } from 'react';
import {
  Upload,
  Search,
  Trash2,
  Download,
  Image,
  FileVideo,
  File,
  Music,
  Grid,
  List,
  HardDrive,
  Loader2
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useListMediaLibraryQuery, MediaLibraryItem } from '@/client/api/whatsapp/list-media-library.query';
import { useUploadMediaMutation } from '@/client/api/whatsapp/upload-media.mutation';
import { useDeleteMediaLibraryMutation } from '@/client/api/whatsapp/delete-media-library.mutation';
import { useGetMediaUrlMutation } from '@/client/api/whatsapp/get-media-url.mutation';
import ConfirmDeleteDialog from '@/client/components/admin/WhatsApp/ConfirmDeleteDialog';

type MediaType = 'image' | 'video' | 'document' | 'audio';

const typeIcons: Record<MediaType, any> = { image: Image, video: FileVideo, document: File, audio: Music };
const typeColors: Record<MediaType, string> = {
  image: 'bg-blue-50', video: 'bg-purple-50', document: 'bg-orange-50', audio: 'bg-green-50'
};
const typeBadges: Record<MediaType, string> = {
  image: 'bg-blue-100 text-blue-700', video: 'bg-purple-100 text-purple-700',
  document: 'bg-orange-100 text-orange-700', audio: 'bg-green-100 text-green-700'
};

const typeFor = (mimeType: string): MediaType => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MediaLibraryPage: React.FC = () => {
  const { data, isLoading, refetch } = useListMediaLibraryQuery();
  const media = data?.data?.media || [];
  const uploadMutation = useUploadMediaMutation();
  const deleteMutation = useDeleteMediaLibraryMutation();
  const getUrlMutation = useGetMediaUrlMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; fileName: string } | null>(null);
  const isUploading = uploadProgress !== null;
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MediaLibraryItem | null>(null);

  const filtered = media.filter(m => {
    const type = typeFor(m.mime_type);
    const matchesSearch = m.filename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    let failures = 0;

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      setUploadProgress({ current: i + 1, total: fileList.length, fileName: file.name });

      const form = new FormData();
      form.append('file', file);
      form.append('mime_type', file.type || 'application/octet-stream');
      form.append('filename', file.name);
      try {
        await uploadMutation.mutateAsync(form);
      } catch (error) {
        failures++;
        toast.error(error instanceof Error ? `${file.name}: ${error.message}` : `Failed to upload ${file.name}`);
      }
    }

    setUploadProgress(null);
    const succeeded = fileList.length - failures;
    if (succeeded > 0) {
      toast.success(succeeded === 1 ? 'File uploaded' : `${succeeded} files uploaded`);
    }
    refetch();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.library_id);
    deleteMutation.mutate(
      { library_id: deleteTarget.library_id },
      {
        onSuccess: () => {
          toast.success(`${deleteTarget.filename} removed`);
          setDeleteTarget(null);
          refetch();
        },
        onSettled: () => setDeletingId(null),
      }
    );
  };

  const handleDownload = (item: MediaLibraryItem) => {
    setDownloadingId(item.library_id);
    getUrlMutation.mutate(
      { media_id: item.media_id },
      {
        onSuccess: (res) => {
          if (res.data?.url) window.open(res.data.url, '_blank');
          else toast.error('No download link returned');
        },
        onError: (error) => toast.error(error.message || 'Failed to get the download link'),
        onSettled: () => setDownloadingId(null),
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
            <p className="text-sm text-gray-500 mt-1">Manage media files for WhatsApp messages</p>
          </div>
          <label className={`px-4 py-2 bg-green-500 text-white rounded-lg transition-colors flex items-center gap-2 ${isUploading ? 'opacity-75 cursor-not-allowed' : 'hover:bg-green-600 cursor-pointer'}`}>
            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {isUploading ? `Uploading ${uploadProgress!.current}/${uploadProgress!.total}...` : 'Upload Files'}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
              className="hidden"
              disabled={isUploading}
              onChange={(e) => handleUpload(e.target.files)}
            />
          </label>
        </div>
      </div>

      <div className="p-6">
        {isUploading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Uploading file {uploadProgress!.current} of {uploadProgress!.total}
              </p>
              <p className="text-xs text-blue-700 truncate">{uploadProgress!.fileName}</p>
            </div>
            <div className="w-32 h-1.5 bg-blue-100 rounded-full overflow-hidden flex-shrink-0">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${(uploadProgress!.current / uploadProgress!.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {(['image', 'video', 'document', 'audio'] as MediaType[]).map(type => {
            const count = media.filter(m => typeFor(m.mime_type) === type).length;
            const Icon = typeIcons[type];
            return (
              <div key={type} className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${typeColors[type]} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900">{count}</p>
                    <p className="text-xs text-gray-500 capitalize">{type}s</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg border border-gray-200 mb-4 p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="document">Documents</option>
              <option value="audio">Audio</option>
            </select>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button onClick={() => setViewMode('grid')} className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                <Grid className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                <List className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {isLoading && <p className="text-sm text-gray-500 mb-4">Loading...</p>}

        {/* Grid View */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map(item => {
              const type = typeFor(item.mime_type);
              const Icon = typeIcons[type];
              return (
                <div key={item.library_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group">
                  <div className={`h-32 ${typeColors[type]} flex items-center justify-center relative`}>
                    <Icon className="w-8 h-8" />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 gap-2">
                      <button
                        onClick={() => handleDownload(item)}
                        disabled={downloadingId === item.library_id || deletingId === item.library_id}
                        className="p-1.5 bg-white rounded-lg shadow disabled:opacity-50"
                      >
                        {downloadingId === item.library_id
                          ? <Loader2 className="w-4 h-4 text-gray-700 animate-spin" />
                          : <Download className="w-4 h-4 text-gray-700" />}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(item)}
                        disabled={deletingId === item.library_id || downloadingId === item.library_id}
                        className="p-1.5 bg-white rounded-lg shadow disabled:opacity-50"
                      >
                        {deletingId === item.library_id
                          ? <Loader2 className="w-4 h-4 text-red-600 animate-spin" />
                          : <Trash2 className="w-4 h-4 text-red-600" />}
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.filename}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`px-1.5 py-0.5 rounded text-xs capitalize ${typeBadges[type]}`}>{type}</span>
                      <span className="text-xs text-gray-500">{formatSize(item.size)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filtered.map(item => {
                    const type = typeFor(item.mime_type);
                    const Icon = typeIcons[type];
                    return (
                      <tr key={item.library_id} className="hover:bg-gray-50">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 ${typeColors[type]} rounded-lg flex items-center justify-center`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <span className="text-sm font-medium text-gray-900">{item.filename}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs capitalize ${typeBadges[type]}`}>{type}</span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-500">{formatSize(item.size)}</td>
                        <td className="px-6 py-3 text-sm text-gray-500">{moment.unix(item.uploaded_at).format('YYYY-MM-DD')}</td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleDownload(item)}
                              disabled={downloadingId === item.library_id || deletingId === item.library_id}
                              className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                            >
                              {downloadingId === item.library_id
                                ? <Loader2 className="w-4 h-4 text-gray-600 animate-spin" />
                                : <Download className="w-4 h-4 text-gray-600" />}
                            </button>
                            <button
                              onClick={() => setDeleteTarget(item)}
                              disabled={deletingId === item.library_id || downloadingId === item.library_id}
                              className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                            >
                              {deletingId === item.library_id
                                ? <Loader2 className="w-4 h-4 text-red-600 animate-spin" />
                                : <Trash2 className="w-4 h-4 text-red-600" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <HardDrive className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No media files found</h3>
            <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remove Media File"
        itemLabel={deleteTarget?.filename || 'this file'}
        description="This removes it from the library. It won't delete the file from Meta's servers."
        onConfirm={confirmDelete}
        isPending={deleteMutation.isPending}
        errorMessage={deleteMutation.error?.message}
      />
    </div>
  );
};

export default MediaLibraryPage;
